import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Zap, Copy, Check, X, Loader2, Wifi, WifiOff, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const CHUNK_SIZE = 64 * 1024;

const P2PTransferModal = ({ files, token, onClose }) => {
  const [roomId, setRoomId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('creating'); // creating, waiting, connected, transferring, complete, error
  const [errorMsg, setErrorMsg] = useState('');

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const transferCompleteRef = useRef(false);

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} Mo`;
    return `${(bytes / 1073741824).toFixed(2)} Go`;
  };

  // Create room on mount
  useEffect(() => {
    createRoom();
    return () => {
      wsRef.current?.close();
      pcRef.current?.close();
    };
  }, []);

  const createRoom = async () => {
    try {
      const res = await axios.post(`${API}/p2p/create-room`, {
        filename: files.length === 1 ? files[0].name : `${files.length} fichiers`,
        file_size: totalSize,
        file_count: files.length
      }, { headers: { Authorization: `Bearer ${token}` } });

      const rid = res.data.room_id;
      setRoomId(rid);
      const url = `${window.location.origin}/p2p/${rid}`;
      setShareUrl(url);
      setStatus('waiting');
      connectWebSocket(rid);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.response?.data?.detail || 'Impossible de creer la salle P2P');
    }
  };

  const iceCandidateQueueRef = useRef([]);
  const negotiatingRef = useRef(false);

  const processIceCandidateQueue = async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    while (iceCandidateQueueRef.current.length > 0) {
      const candidate = iceCandidateQueueRef.current.shift();
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { console.warn('ICE error:', e); }
    }
  };

  const connectWebSocket = useCallback((rid) => {
    const ws = new WebSocket(`${WS_URL}/api/ws/p2p/${rid}/sender`);
    wsRef.current = ws;

    ws.onmessage = async (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === 'peer-connected' && !negotiatingRef.current) {
        negotiatingRef.current = true;
        setPeerConnected(true);
        setStatus('connected');
        await createPeerConnection(ws);
      }

      if (msg.type === 'answer') {
        const pc = pcRef.current;
        if (pc && pc.signalingState === 'have-local-offer') {
          await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
          await processIceCandidateQueue();
        }
      }

      if (msg.type === 'ice-candidate' && msg.candidate) {
        if (pcRef.current && pcRef.current.remoteDescription) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch (e) { console.warn('ICE error:', e); }
        } else {
          iceCandidateQueueRef.current.push(msg.candidate);
        }
      }

      if (msg.type === 'peer-disconnected') {
        setPeerConnected(false);
        if (!transferCompleteRef.current) {
          negotiatingRef.current = false;
          setStatus('waiting');
        }
      }
    };

    ws.onerror = () => {
      if (!transferCompleteRef.current) {
        setStatus('error');
        setErrorMsg('Connexion WebSocket perdue');
      }
    };

    ws.onclose = () => {
      if (!transferCompleteRef.current) setPeerConnected(false);
    };
  }, []);

  const createPeerConnection = async (ws) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ]
    });
    pcRef.current = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        ws.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        setStatus('error');
        setErrorMsg('La connexion P2P a echoue. Verifiez votre reseau.');
      }
    };

    const dc = pc.createDataChannel('fileTransfer', { ordered: true });
    dc.binaryType = 'arraybuffer';
    dcRef.current = dc;

    dc.onopen = () => {
      setStatus('transferring');
      sendFiles(dc, ws);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    ws.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription }));
  };

  const sendFiles = async (dc, ws) => {
    let totalSent = 0;

    for (let fileIdx = 0; fileIdx < files.length; fileIdx++) {
      const file = files[fileIdx];

      dc.send(JSON.stringify({
        type: 'file-meta',
        name: file.name,
        size: file.size,
        mimeType: file.type,
        index: fileIdx,
        total: files.length
      }));

      await new Promise(r => setTimeout(r, 100));

      const reader = file.stream().getReader();
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          for (let offset = 0; offset < value.length; offset += CHUNK_SIZE) {
            const chunk = value.slice(offset, offset + CHUNK_SIZE);

            while (dc.bufferedAmount > 4 * 1024 * 1024) {
              await new Promise(r => setTimeout(r, 50));
            }

            dc.send(chunk);
            totalSent += chunk.length;
            const pct = Math.round((totalSent / totalSize) * 100);
            setProgress(pct);

            ws.send(JSON.stringify({ type: 'transfer-progress', progress: pct }));
          }
        }
      }

      dc.send(JSON.stringify({ type: 'file-end', index: fileIdx }));
      await new Promise(r => setTimeout(r, 200));
    }

    dc.send(JSON.stringify({ type: 'transfer-done' }));
    ws.send(JSON.stringify({ type: 'transfer-complete' }));
    transferCompleteRef.current = true;
    setStatus('complete');
    setProgress(100);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="p2p-transfer-modal">
      <div className="bg-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl shadow-purple-500/10 w-full max-w-md mx-4 relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-purple-500/15">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-400" />
            <span className="text-white font-semibold">Transfert P2P</span>
          </div>
          {status !== 'transferring' && (
            <button onClick={onClose} className="text-blue-300/50 hover:text-white transition-colors" data-testid="p2p-modal-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* File info */}
          <div className="text-center text-sm text-blue-300/60">
            {files.length} fichier(s) &middot; {formatSize(totalSize)}
          </div>

          {/* Creating room */}
          {status === 'creating' && (
            <div className="text-center py-6">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
              <p className="text-blue-300/70 text-sm">Creation de la salle P2P...</p>
            </div>
          )}

          {/* Waiting for receiver */}
          {status === 'waiting' && (
            <div className="space-y-5">
              <div className="flex items-center justify-center gap-2 text-orange-400">
                <WifiOff className="w-5 h-5" />
                <span className="font-medium text-sm">En attente du destinataire...</span>
              </div>

              <div className="space-y-2">
                <p className="text-xs text-blue-300/50 text-center">Partagez ce lien</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-slate-800/80 border border-purple-500/20 rounded-lg px-3 py-2 text-white text-xs font-mono truncate focus:outline-none"
                    data-testid="p2p-share-url"
                  />
                  <Button onClick={copyUrl} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white shrink-0" data-testid="p2p-copy-url">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-xl">
                  <QRCodeSVG value={shareUrl} size={120} level="H" />
                </div>
              </div>
            </div>
          )}

          {/* Connected, establishing WebRTC */}
          {status === 'connected' && (
            <div className="text-center py-6 space-y-3">
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <Wifi className="w-5 h-5" />
                <span className="font-medium text-sm">Destinataire connecte !</span>
              </div>
              <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto" />
              <p className="text-xs text-blue-300/50">Etablissement de la connexion P2P...</p>
            </div>
          )}

          {/* Transferring */}
          {status === 'transferring' && (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-center gap-2 text-purple-400">
                <Zap className="w-5 h-5 animate-pulse" />
                <span className="font-medium text-sm">Transfert en cours...</span>
              </div>
              <div className="relative w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-white font-bold text-xl">{progress}%</p>
              <p className="text-center text-xs text-blue-300/40">Transfert direct, sans passer par nos serveurs</p>
            </div>
          )}

          {/* Complete */}
          {status === 'complete' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Transfert termine !</h2>
              <p className="text-sm text-blue-300/50">{files.length} fichier(s) envoye(s) &middot; {formatSize(totalSize)}</p>
              <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="p2p-done-btn">
                Fermer
              </Button>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="text-center py-4 space-y-3">
              <WifiOff className="w-10 h-10 text-red-400 mx-auto" />
              <p className="text-white font-medium">Erreur</p>
              <p className="text-sm text-red-300/70">{errorMsg}</p>
              <Button onClick={onClose} variant="outline" className="border-red-500/30 text-red-300">
                Fermer
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default P2PTransferModal;
